import {
  type AudioClip,
  audioClipEnd,
  type ClipColor,
  type ClipLayout,
  COLOR_LOOKS,
  colorFromLook,
  DEFAULT_TRANSITION_MS,
  deviceModel,
  devicePerspectives,
  formatTimecode,
  groupSceneLayouts,
  groupTrackNumbers,
  isNeutralColor,
  layoutClips,
  MIN_CLIP_MS,
  matchingPerspective,
  normalizeColor,
  OVERLAY_LAYOUT_LABELS,
  type OverlayClip,
  overlayClipEnd,
  overlayLayoutsForFrame,
  type SceneDocument,
  type SceneElement,
  type SceneLayout,
  sceneLayoutsForFrame,
  setElementTime,
  TRANSITION_KINDS,
  TRANSITION_LABELS,
  trackCount,
  trackEntries,
  trackInfo,
  type VideoClip,
  videoDuration,
} from "@cva/shared";
import { useQueryClient } from "@tanstack/react-query";
import {
  ArrowDown,
  ArrowDownToLine,
  ArrowRightToLine,
  ArrowUp,
  ArrowUpToLine,
  AudioLines,
  Blend,
  BookmarkPlus,
  Captions,
  ChevronsDownUp,
  ChevronsUpDown,
  ClipboardPaste,
  Copy,
  CopyPlus,
  Crosshair,
  Eye,
  EyeOff,
  Film,
  FolderOutput,
  FolderPlus,
  Gauge,
  Group,
  Layers,
  LayoutPanelTop,
  Loader2,
  Lock,
  LockOpen,
  Maximize2,
  Move,
  Palette,
  PanelBottom,
  PencilLine,
  PersonStanding,
  PictureInPicture2,
  Plus,
  Scissors,
  SlidersHorizontal,
  Smartphone,
  Sparkles,
  Trash2,
  Type,
  Ungroup,
  Volume2,
  VolumeX,
  Wand2,
  X,
} from "lucide-react";
import type { ReactNode } from "react";
import { toast } from "sonner";
import {
  DropdownCheckItem,
  DropdownContent,
  DropdownItem,
  DropdownLabel,
  DropdownMenu,
  DropdownSeparator,
  DropdownSub,
  DropdownSubContent,
  DropdownSubTrigger,
  DropdownTrigger,
} from "../../components/ui/dropdown";
import { canGroup } from "../ContextMenu";
import type { AssetInfo } from "../commands";
import {
  copySelection,
  deleteSelection,
  duplicateSelection as duplicateElements,
  groupSelection,
  pasteData,
  toggleHidden,
  toggleLock,
  ungroupSelection,
} from "../commands";
import { startCutout } from "../cutout";
import { useEnv } from "../env";
import { useLeftNav } from "../left-nav";
import { useLibraryItems } from "../library";
import { requestSaveBlock } from "../SaveBlockDialog";
import { type EditorStore, useEditor, useEditorApi } from "../store";
import { openCaptions } from "./Captions";
import { colorFilterUrl } from "./color-filters";
import {
  applyColorEverywhere,
  applyLayoutAt,
  applyOverlayLayout,
  applyTransition,
  applyTransitionEverywhere,
  type CornerShape,
  defaultClipDevice,
  deleteSelectedClip,
  duplicateSelectedClip,
  moveSelectedToMain,
  moveSelectedToTrack,
  overlayBoxShape,
  overlayCornerShape,
  removeSilences,
  setCaptionsHidden,
  setClipColor,
  setOverlayCorners,
  setOverlayDevice,
  showMainClipInDevice,
  splitAtPlayhead,
  updateAudioClip,
  updateOverlayClip,
  updateVideoClip,
} from "./commands";
import { useCaptions } from "./data";
import { LayoutPicture, OverlayLayoutPicture } from "./LayoutPictures";
import { BehindPersonItem } from "./PersonCutoutSection";
import {
  addTitleBehindPerson,
  type ClipRef,
  canCutOutPerson,
  cutOutPerson,
  setClipCutout,
} from "./person-cutout";
import { useVideo, useVideoApi } from "./store";
import {
  addTrack,
  deleteGroup,
  deleteTrack,
  groupTrackRows,
  moveTrackRows,
  setGroupCollapsed,
  toggleGroupHidden,
  toggleTrackHidden,
  ungroupTrackRows,
} from "./track-commands";

/**
 * Right-click menu of the timeline: the settings of the clip, element or
 * track under the pointer, right where one works – screen layouts and picture
 * in picture, captions, transitions, looks, speed, sound, split, duplicate,
 * delete. Every change goes through the document store (undo, live editing).
 */

export type TimelineMenuTarget =
  | { kind: "clip"; id: string }
  | { kind: "overlay"; id: string }
  | { kind: "audio"; id: string }
  | { kind: "element"; id: string }
  | { kind: "captions" }
  /** A track header; `selected`: the selected tracks (to group them). */
  | { kind: "track"; track: number; selected?: number[] }
  | { kind: "group"; id: string }
  | { kind: "empty" };

export interface TimelineMenuState {
  x: number;
  y: number;
  /** Timeline position under the pointer (ms). */
  t: number;
  target: TimelineMenuTarget;
}

/** Reads the target from the `data-menu` attribute of the item under the pointer. */
export function menuTargetOf(element: Element | null): TimelineMenuTarget {
  const value = element?.closest<HTMLElement>("[data-menu]")?.dataset.menu ?? "empty";
  const [kind, id = ""] = value.split(":");
  if ((kind === "clip" || kind === "overlay" || kind === "audio" || kind === "element") && id) {
    return { kind, id };
  }
  if (kind === "track" && Number(id) >= 1) return { kind, track: Number(id) };
  if (kind === "group" && id) return { kind, id };
  return kind === "captions" ? { kind: "captions" } : { kind: "empty" };
}

const MAIN_SPEEDS = [0.5, 0.75, 1, 1.5, 2, 3];
const OVERLAY_SPEEDS = [0.5, 1, 1.5, 2];
const AUDIO_VOLUMES = [0.25, 0.5, 0.75, 1, 1.5];
const SILENCE_MS = 700;

function Shortcut({ children }: { children: ReactNode }) {
  return <span className="ml-auto pl-6 text-xs text-zinc-400">{children}</span>;
}

const speedLabel = (speed: number) =>
  speed === 1 ? "Normal (1×)" : `${speed.toLocaleString("de-DE")}×`;

export function TimelineMenu({
  menu,
  onClose,
  onRenameTrack,
}: {
  menu: TimelineMenuState | null;
  onClose: () => void;
  /** Starts renaming a track (`track:n`) or group (`group:id`) in its header. */
  onRenameTrack?: (key: string) => void;
}) {
  if (!menu) return null;
  // A new key per opening: the menu starts closed-over at the new position.
  return (
    <MenuBody
      key={`${menu.x}-${menu.y}-${menu.target.kind}`}
      menu={menu}
      onClose={onClose}
      onRename={onRenameTrack}
    />
  );
}

function MenuBody({
  menu,
  onClose,
  onRename,
}: {
  menu: TimelineMenuState;
  onClose: () => void;
  onRename: ((key: string) => void) | undefined;
}) {
  const readOnly = useEditor((s) => s.readOnly);
  const video = useVideoApi()!;
  const { target } = menu;
  let content: ReactNode;
  if (readOnly) {
    content = (
      <DropdownItem onSelect={() => video.getState().seek(menu.t)}>
        <Crosshair /> Abspielkopf hierher
      </DropdownItem>
    );
  } else if (target.kind === "clip") content = <ClipMenu id={target.id} />;
  else if (target.kind === "overlay") content = <OverlayMenu id={target.id} />;
  else if (target.kind === "audio") content = <AudioMenu id={target.id} />;
  else if (target.kind === "element") content = <ElementMenu id={target.id} />;
  else if (target.kind === "captions") content = <CaptionsMenu />;
  else if (target.kind === "track") {
    content = (
      <TrackMenu
        track={target.track}
        selected={target.selected ?? [target.track]}
        onRename={() => onRename?.(`track:${target.track}`)}
      />
    );
  } else if (target.kind === "group") {
    content = <GroupMenu id={target.id} onRename={() => onRename?.(`group:${target.id}`)} />;
  } else content = <EmptyMenu t={menu.t} />;
  return (
    <DropdownMenu open onOpenChange={(open) => !open && onClose()} modal={false}>
      <DropdownTrigger asChild>
        <span
          aria-hidden="true"
          style={{ position: "fixed", left: menu.x, top: menu.y, width: 1, height: 1 }}
        />
      </DropdownTrigger>
      <DropdownContent
        align="start"
        className="max-h-[var(--radix-dropdown-menu-content-available-height)] min-w-60 overflow-y-auto"
      >
        {content}
      </DropdownContent>
    </DropdownMenu>
  );
}

// ---------------------------------------------------------------------------
// Tracks and groups (headers on the left)
// ---------------------------------------------------------------------------

function TrackMenu({
  track,
  selected,
  onRename,
}: {
  track: number;
  selected: number[];
  onRename: () => void;
}) {
  const store = useEditorApi();
  const doc = useEditor((s) => s.doc);
  const info = trackInfo(doc, track);
  const count = trackCount(doc);
  const items = trackEntries(doc).filter((entry) => entry.track === track).length;
  const grouped = info?.group ? doc.timeline?.trackGroups?.find((g) => g.id === info.group) : null;
  const tracks = selected.includes(track) ? selected : [track];
  const person = info?.kind === "person";
  return (
    <>
      <DropdownLabel>
        {info?.name ?? (person ? "Person (freigestellt)" : `Spur ${track + 1}`)}
        {person
          ? " · darunter liegt alles hinter ihr"
          : items > 0
            ? ` · ${items} ${items === 1 ? "Objekt" : "Objekte"}`
            : " · leer"}
      </DropdownLabel>
      <DropdownItem onSelect={() => addTrack(store, track + 1)}>
        <ArrowUpToLine /> Neue Spur darüber
      </DropdownItem>
      <DropdownItem onSelect={() => addTrack(store, track)}>
        <ArrowDownToLine /> Neue Spur darunter
      </DropdownItem>
      <DropdownSeparator />
      <DropdownItem
        disabled={track >= count}
        onSelect={() => moveTrackRows(store, track, 1, track + 2)}
      >
        <ArrowUp /> Spur nach oben
      </DropdownItem>
      <DropdownItem
        disabled={track <= 1}
        onSelect={() => moveTrackRows(store, track, 1, track - 1)}
      >
        <ArrowDown /> Spur nach unten
      </DropdownItem>
      <DropdownItem onSelect={onRename}>
        <PencilLine /> Umbenennen
      </DropdownItem>
      <DropdownItem disabled={person} onSelect={() => toggleTrackHidden(store, track)}>
        {info?.hidden ? <Eye /> : <EyeOff />} {info?.hidden ? "Spur zeigen" : "Spur ausblenden"}
      </DropdownItem>
      <DropdownSeparator />
      <DropdownItem
        onSelect={() => {
          const group = groupTrackRows(store, tracks);
          if (group)
            toast.success(
              tracks.length > 1 ? `${tracks.length} Spuren gruppiert` : "Gruppe erstellt",
            );
        }}
      >
        <FolderPlus />{" "}
        {tracks.length > 1 ? `${tracks.length} Spuren gruppieren` : "Gruppe aus dieser Spur"}
        <Shortcut>⇧-Klick: mehrere</Shortcut>
      </DropdownItem>
      {grouped ? (
        <DropdownItem
          onSelect={() => moveTrackRows(store, track, 1, track + 1 + groupAbove(doc, track))}
        >
          <FolderOutput /> Aus „{grouped.name}“ herausnehmen
        </DropdownItem>
      ) : null}
      {person ? null : <DropdownSeparator />}
      {person ? null : (
        <DropdownItem
          danger
          onSelect={() => {
            deleteTrack(store, track);
            if (items > 0)
              toast.info(
                `Spur mit ${items} ${items === 1 ? "Objekt" : "Objekten"} gelöscht – ⌘Z macht es rückgängig`,
              );
          }}
        >
          <Trash2 /> Spur löschen
        </DropdownItem>
      )}
    </>
  );
}

/** Tracks of the same group above `track` (to move it out on top of the group). */
function groupAbove(doc: SceneDocument, track: number): number {
  const group = trackInfo(doc, track)?.group;
  let above = 0;
  while (group && trackInfo(doc, track + above + 1)?.group === group) above++;
  return above;
}

function GroupMenu({ id, onRename }: { id: string; onRename: () => void }) {
  const store = useEditorApi();
  const doc = useEditor((s) => s.doc);
  const group = doc.timeline?.trackGroups?.find((item) => item.id === id);
  if (!group) return <DropdownLabel>Gruppe</DropdownLabel>;
  const tracks = groupTrackNumbers(doc, id);
  const hidden = tracks.every((track) => trackInfo(doc, track)?.hidden);
  return (
    <>
      <DropdownLabel>
        {group.name} · {tracks.length} {tracks.length === 1 ? "Spur" : "Spuren"}
      </DropdownLabel>
      <DropdownItem onSelect={() => setGroupCollapsed(store, id, !group.collapsed)}>
        {group.collapsed ? <ChevronsUpDown /> : <ChevronsDownUp />}{" "}
        {group.collapsed ? "Aufklappen" : "Zuklappen"}
      </DropdownItem>
      <DropdownItem onSelect={onRename}>
        <PencilLine /> Umbenennen
      </DropdownItem>
      <DropdownItem onSelect={() => toggleGroupHidden(store, id)}>
        {hidden ? <Eye /> : <EyeOff />} {hidden ? "Gruppe zeigen" : "Gruppe ausblenden"}
      </DropdownItem>
      <DropdownItem onSelect={() => addTrack(store, Math.max(...tracks))}>
        <Plus /> Neue Spur in der Gruppe
      </DropdownItem>
      <DropdownSeparator />
      <DropdownItem onSelect={() => ungroupTrackRows(store, id)}>
        <FolderOutput /> Gruppe auflösen (Spuren bleiben)
      </DropdownItem>
      <DropdownItem danger onSelect={() => deleteGroup(store, id)}>
        <Trash2 /> Gruppe mit allen Spuren löschen
      </DropdownItem>
    </>
  );
}

// ---------------------------------------------------------------------------
// Shared pieces
// ---------------------------------------------------------------------------

function usePlayheadIn(start: number, end: number) {
  return useVideo((s) => s.time > start + 1 && s.time < end - 1);
}

/** Whether the speech recognition still runs for an asset (or any clip of the main track). */
function useRecognising(assetId?: string) {
  const { infos } = useCaptions();
  const timeline = useEditor((s) => s.doc.timeline);
  const ids = assetId ? [assetId] : (timeline?.clips ?? []).map((clip) => clip.assetId);
  return ids.some((id) => {
    const status = infos.get(id)?.status;
    return status === "queued" || status === "processing";
  });
}

/** Whether captions exist or can be added (a video on the main track). */
function useCaptionsAvailable() {
  const { captionsElement } = useCaptions();
  const hasVideo = useEditor((s) =>
    (s.doc.timeline?.clips ?? []).some((clip) => clip.kind === "video"),
  );
  return Boolean(captionsElement) || hasVideo;
}

/** Show / hide the captions; adds them to the video on first use. */
function CaptionItems({ assetId, muted }: { assetId?: string; muted?: boolean }) {
  const store = useEditorApi();
  const video = useVideoApi()!;
  const { captionsElement } = useCaptions();
  const recognising = useRecognising(assetId);
  return (
    <>
      {!captionsElement ? (
        <DropdownItem onSelect={() => openCaptions(store, video)}>
          <Captions /> Untertitel einblenden
        </DropdownItem>
      ) : captionsElement.hidden ? (
        <DropdownItem onSelect={() => setCaptionsHidden(store, false)}>
          <Eye /> Untertitel einblenden
        </DropdownItem>
      ) : (
        <DropdownItem onSelect={() => setCaptionsHidden(store, true)}>
          <EyeOff /> Untertitel ausblenden
        </DropdownItem>
      )}
      {recognising ? (
        <DropdownItem disabled>
          <Loader2 className="animate-spin" /> Untertitel werden erkannt …
        </DropdownItem>
      ) : muted ? (
        <DropdownItem disabled>
          <VolumeX /> Clip ist stumm – keine Untertitel
        </DropdownItem>
      ) : null}
      {captionsElement ? (
        <DropdownItem onSelect={() => openCaptions(store, video, { tab: "text" })}>
          <PencilLine /> Untertitel bearbeiten
        </DropdownItem>
      ) : null}
    </>
  );
}

/** Colour looks: the organization's saved ones, then the built-in ones. */
function LookSubmenu({
  clip,
  track,
  thumbnail,
}: {
  clip: VideoClip | OverlayClip;
  track: "video" | "overlay";
  thumbnail: string | null;
}) {
  const store = useEditorApi();
  const env = useEnv();
  const saved = useLibraryItems(env.orgId, "look");
  const options = [
    ...saved.items.map((item) => ({
      id: `saved:${item.id}`,
      label: item.name,
      values: item.data.values as Partial<ClipColor>,
    })),
    ...COLOR_LOOKS.map((look) => ({ id: look.id, label: look.label, values: look.values })),
  ];
  const active = clip.color?.look ?? (isNeutralColor(clip.color) ? "original" : null);
  return (
    <DropdownSub>
      <DropdownSubTrigger>
        <Palette /> Farblook
      </DropdownSubTrigger>
      <DropdownSubContent className="w-56">
        {options.map((option) => (
          <DropdownCheckItem
            key={option.id}
            checked={active === option.id}
            onSelect={() => setClipColor(store, track, clip.id, colorFromLook(option))}
          >
            <LookSwatch values={option.values} thumbnail={thumbnail} />
            <span className="truncate">{option.label}</span>
          </DropdownCheckItem>
        ))}
        <DropdownSeparator />
        <DropdownItem
          onSelect={() => {
            const count = applyColorEverywhere(store, clip.color);
            toast.success(`Farbe auf ${count} ${count === 1 ? "Clip" : "Clips"} übertragen`);
          }}
        >
          <CopyPlus /> Auf alle Clips übertragen
        </DropdownItem>
      </DropdownSubContent>
    </DropdownSub>
  );
}

function LookSwatch({
  values,
  thumbnail,
}: {
  values: Partial<ClipColor>;
  thumbnail: string | null;
}) {
  const filter = colorFilterUrl(normalizeColor(values)) ?? undefined;
  return (
    <span
      className="size-5 shrink-0 rounded bg-zinc-800 bg-cover bg-center"
      style={{
        backgroundImage: thumbnail
          ? `url("${thumbnail}")`
          : "linear-gradient(135deg, #f59e0b, #10b981 45%, #3b82f6)",
        filter,
      }}
    />
  );
}

function SpeedSubmenu({
  speeds,
  value,
  onPick,
}: {
  speeds: number[];
  value: number;
  onPick: (speed: number) => void;
}) {
  return (
    <DropdownSub>
      <DropdownSubTrigger>
        <Gauge /> Geschwindigkeit
        <span className="text-xs text-zinc-400">{value.toLocaleString("de-DE")}×</span>
      </DropdownSubTrigger>
      <DropdownSubContent>
        {speeds.map((speed) => (
          <DropdownCheckItem key={speed} checked={value === speed} onSelect={() => onPick(speed)}>
            {speedLabel(speed)}
          </DropdownCheckItem>
        ))}
      </DropdownSubContent>
    </DropdownSub>
  );
}

/** "Hintergrund entfernen" for a still image (like the canvas and the properties). */
function CutoutItem({
  asset,
  target,
}: {
  asset: AssetInfo | undefined;
  target: { kind: "clip" | "overlay"; id: string };
}) {
  const store = useEditorApi();
  const env = useEnv();
  const queryClient = useQueryClient();
  if (asset?.kind !== "image" || asset.cutout || asset.status === "processing") return null;
  return (
    <DropdownItem
      onSelect={() =>
        void startCutout({ orgId: env.orgId, store, queryClient, target, source: asset })
      }
    >
      <Wand2 /> Hintergrund entfernen
    </DropdownItem>
  );
}

/** "Person freistellen" for a video clip, or its settings once it is on. */
function PersonItems({
  clip,
  clipRef,
  asset,
}: {
  clip: VideoClip | OverlayClip;
  clipRef: ClipRef;
  asset: AssetInfo | undefined;
}) {
  const store = useEditorApi();
  const env = useEnv();
  const queryClient = useQueryClient();
  if (!canCutOutPerson(clip) || !asset) return null;
  const start = (background: "keep" | "remove") =>
    void cutOutPerson({ orgId: env.orgId, store, queryClient, ref: clipRef, asset, background });
  if (!clip.cutout) {
    return (
      <DropdownItem onSelect={() => start("keep")}>
        <PersonStanding /> Person freistellen
      </DropdownItem>
    );
  }
  const background = clip.cutout.background;
  return (
    <DropdownSub>
      <DropdownSubTrigger>
        <PersonStanding /> Person freigestellt
        {asset.matte?.status === "processing" ? (
          <Loader2 className="ml-auto animate-spin text-zinc-400" />
        ) : null}
      </DropdownSubTrigger>
      <DropdownSubContent>
        <DropdownCheckItem checked={background === "keep"} onSelect={() => start("keep")}>
          Mit Hintergrund
        </DropdownCheckItem>
        <DropdownCheckItem checked={background === "remove"} onSelect={() => start("remove")}>
          Nur Person (ohne Hintergrund)
        </DropdownCheckItem>
        <DropdownSeparator />
        <DropdownItem onSelect={() => addTitleBehindPerson(store, clipRef)}>
          <Type /> Titel hinter die Person setzen
        </DropdownItem>
        <DropdownItem onSelect={() => setClipCutout(store, clipRef, null)}>
          <X /> Freistellung ausschalten
        </DropdownItem>
      </DropdownSubContent>
    </DropdownSub>
  );
}

function SplitItem({ inside }: { inside: boolean }) {
  const store = useEditorApi();
  const video = useVideoApi()!;
  if (!inside) return null;
  return (
    <DropdownItem onSelect={() => splitAtPlayhead(store, video)}>
      <Scissors /> Am Abspielkopf teilen <Shortcut>S</Shortcut>
    </DropdownItem>
  );
}

function DeleteClipItem() {
  const store = useEditorApi();
  const video = useVideoApi()!;
  return (
    <DropdownItem danger onSelect={() => deleteSelectedClip(store, video)}>
      <Trash2 /> Löschen <Shortcut>⌫</Shortcut>
    </DropdownItem>
  );
}

// ---------------------------------------------------------------------------
// Main track clip
// ---------------------------------------------------------------------------

function ClipMenu({ id }: { id: string }) {
  const store = useEditorApi();
  const video = useVideoApi()!;
  const env = useEnv();
  const timeline = useEditor((s) => s.doc.timeline);
  const layout = layoutClips(timeline);
  const entry = layout.find((item) => item.clip.id === id);
  const inside = usePlayheadIn(entry?.start ?? 0, entry?.end ?? 0);
  const captions = useCaptionsAvailable();
  if (!entry) return null;
  const { clip } = entry;
  const asset = env.assets.get(clip.assetId);
  const isVideo = clip.kind === "video";
  const hasAudio = isVideo && asset?.media?.hasAudio !== false;
  const thumbnail = asset?.thumbnailUrl ?? (clip.kind === "image" ? (asset?.url ?? null) : null);
  const update = (recipe: (target: VideoClip) => void) => updateVideoClip(store, id, recipe);
  return (
    <>
      <DropdownLabel>
        {isVideo ? "Videoclip" : "Bild"} · {formatTimecode(entry.end - entry.start)}
      </DropdownLabel>
      <LayoutSubmenu entry={entry} />
      <PipSubmenu entry={entry} />
      {clip.box ? (
        <DropdownItem
          onSelect={() =>
            update((target) => {
              delete target.box;
              delete target.radius;
            })
          }
        >
          <Maximize2 /> Wieder Vollbild
        </DropdownItem>
      ) : null}
      {clip.box || clip.framing.fit === "contain" ? (
        <DropdownCheckItem
          checked={clip.background === "blur"}
          onSelect={() =>
            update((target) => {
              target.background = target.background === "blur" ? "color" : "blur";
            })
          }
          title="Um das Bild herum eine unscharfe Kopie des Videos statt der Hintergrundfarbe"
        >
          Unscharfer Hintergrund
        </DropdownCheckItem>
      ) : null}
      {captions ? (
        <>
          <DropdownSeparator />
          {isVideo ? <CaptionItems assetId={clip.assetId} muted={clip.muted} /> : <CaptionItems />}
        </>
      ) : null}
      <DropdownSeparator />
      <TransitionSubmenu entry={entry} cuts={layout.length - 1} />
      <LookSubmenu clip={clip} track="video" thumbnail={thumbnail} />
      {isVideo ? (
        <SpeedSubmenu
          speeds={MAIN_SPEEDS}
          value={clip.speed}
          onPick={(speed) => update((target) => (target.speed = speed))}
        />
      ) : null}
      {hasAudio ? (
        <DropdownItem onSelect={() => update((target) => (target.muted = !target.muted))}>
          {clip.muted ? <Volume2 /> : <VolumeX />} {clip.muted ? "Ton an" : "Stumm schalten"}
        </DropdownItem>
      ) : null}
      <DropdownItem
        onSelect={() => {
          if (!inside) video.getState().seek(entry.start + entry.overlap);
          video.getState().setFraming(true);
        }}
      >
        <Move /> Bildausschnitt auf der Fläche verschieben
      </DropdownItem>
      <DropdownSeparator />
      <SplitItem inside={inside} />
      <DropdownItem onSelect={() => duplicateSelectedClip(store, video)}>
        <CopyPlus /> Duplizieren <Shortcut>⌘D</Shortcut>
      </DropdownItem>
      <DropdownItem onSelect={() => moveSelectedToTrack(store, video)}>
        <Layers /> Auf eigene Spur legen (Overlay)
      </DropdownItem>
      {/* A device needs a box of its own: the clip moves onto a track above. */}
      <DropdownItem onSelect={() => showMainClipInDevice(store, video, id, asset)}>
        <Smartphone /> Im Gerät zeigen (eigene Spur)
      </DropdownItem>
      <CutoutItem asset={asset} target={{ kind: "clip", id }} />
      <PersonItems clip={clip} clipRef={{ track: "video", id }} asset={asset} />
      <DropdownSeparator />
      <DeleteClipItem />
    </>
  );
}

/** Opens the screen layouts in the left panel (to fill empty areas). */
function openLayouts() {
  useLeftNav.getState().open("blocks", { blocks: "layouts" });
}

/** Applies a screen layout to this clip; empty areas are filled in the "Layouts" view. */
function useApplyLayout(entry: ClipLayout) {
  const store = useEditorApi();
  const video = useVideoApi()!;
  return (layout: SceneLayout) => {
    const result = applyLayoutAt(store, video, layout, entry.start);
    if (result && result.missing.length > 0) {
      openLayouts();
      toast.info(
        result.missing.length === 1
          ? `${layout.label}: Wähle links ein Video oder Bild für den freien Bereich.`
          : `${layout.label}: Wähle links Videos oder Bilder für die ${result.missing.length} freien Bereiche.`,
      );
    }
  };
}

function LayoutTile({
  layout,
  frame,
  spaced,
  onSelect,
}: {
  layout: SceneLayout;
  frame: { width: number; height: number };
  spaced: boolean;
  onSelect: () => void;
}) {
  return (
    <DropdownItem onSelect={onSelect}>
      <span className="flex w-full flex-col items-center gap-1" title={layout.description}>
        <LayoutPicture layout={layout} frame={frame} spaced={spaced} size={40} />
        <span className="line-clamp-2 text-center text-[10px] leading-tight text-zinc-600">
          {layout.label}
        </span>
      </span>
    </DropdownItem>
  );
}

function LayoutSubmenu({ entry }: { entry: ClipLayout }) {
  const video = useVideoApi()!;
  const width = useEditor((s) => s.doc.format.width);
  const height = useEditor((s) => s.doc.format.height);
  const spaced = useVideo((s) => s.layoutSpaced);
  const apply = useApplyLayout(entry);
  const frame = { width, height };
  return (
    <DropdownSub>
      <DropdownSubTrigger>
        <LayoutPanelTop /> Bildaufteilung
      </DropdownSubTrigger>
      <DropdownSubContent className="w-80">
        <DropdownCheckItem
          checked={spaced}
          keepOpen
          onSelect={() => video.getState().setLayoutSpaced(!spaced)}
        >
          Mit Abstand und runden Ecken
        </DropdownCheckItem>
        {groupSceneLayouts(sceneLayoutsForFrame(frame)).map((group) => (
          <div key={group.label}>
            <DropdownLabel>{group.label}</DropdownLabel>
            <div className="grid grid-cols-4 gap-0.5">
              {group.layouts.map((layout) => (
                <LayoutTile
                  key={layout.id}
                  layout={layout}
                  frame={frame}
                  spaced={spaced}
                  onSelect={() => apply(layout)}
                />
              ))}
            </div>
          </div>
        ))}
        <DropdownSeparator />
        <DropdownItem onSelect={() => openLayouts()}>
          <PanelBottom /> Alle Einstellungen unter Bausteine › Layouts
        </DropdownItem>
      </DropdownSubContent>
    </DropdownSub>
  );
}

/** Quick picture in picture: the clip stays full screen, a second picture goes on top. */
function PipSubmenu({ entry }: { entry: ClipLayout }) {
  const width = useEditor((s) => s.doc.format.width);
  const height = useEditor((s) => s.doc.format.height);
  const apply = useApplyLayout(entry);
  const frame = { width, height };
  const layouts = sceneLayoutsForFrame(frame).filter((layout) => layout.pip);
  return (
    <DropdownSub>
      <DropdownSubTrigger>
        <PictureInPicture2 /> Bild im Bild
      </DropdownSubTrigger>
      <DropdownSubContent className="w-64">
        <p className="px-2 pt-1 pb-1.5 text-[11px] leading-snug text-zinc-500">
          Der Clip bleibt im Vollbild, ein zweites Video oder Bild kommt klein darüber (liegt schon
          eins darüber, wird es verwendet).
        </p>
        {layouts.map((layout) => (
          <DropdownItem key={layout.id} onSelect={() => apply(layout)}>
            <LayoutPicture layout={layout} frame={frame} spaced={false} size={28} />
            <span className="truncate">{layout.description}</span>
          </DropdownItem>
        ))}
      </DropdownSubContent>
    </DropdownSub>
  );
}

function TransitionSubmenu({ entry, cuts }: { entry: ClipLayout; cuts: number }) {
  const store = useEditorApi();
  const transition = entry.clip.transition;
  const duration = transition?.duration ?? DEFAULT_TRANSITION_MS;
  if (entry.index === 0) {
    return (
      <DropdownItem disabled>
        <Blend /> Übergang (erst ab dem zweiten Clip)
      </DropdownItem>
    );
  }
  return (
    <DropdownSub>
      <DropdownSubTrigger>
        <Blend /> Übergang
        {transition ? (
          <span className="truncate text-xs text-zinc-400">
            {TRANSITION_LABELS[transition.kind]}
          </span>
        ) : null}
      </DropdownSubTrigger>
      <DropdownSubContent className="w-56">
        <DropdownLabel>Vom Clip davor in diesen</DropdownLabel>
        <DropdownCheckItem
          checked={!transition}
          onSelect={() => applyTransition(store, entry.clip.id, null)}
        >
          Harter Schnitt
        </DropdownCheckItem>
        {TRANSITION_KINDS.map((kind) => (
          <DropdownCheckItem
            key={kind}
            checked={transition?.kind === kind}
            onSelect={() => applyTransition(store, entry.clip.id, { kind, duration })}
          >
            {TRANSITION_LABELS[kind]}
          </DropdownCheckItem>
        ))}
        {transition && cuts > 1 ? (
          <>
            <DropdownSeparator />
            <DropdownItem
              onSelect={() => {
                const count = applyTransitionEverywhere(
                  store,
                  transition.kind,
                  transition.duration,
                );
                toast.success(`${TRANSITION_LABELS[transition.kind]} an ${count} Schnitten`);
              }}
            >
              <CopyPlus /> Für alle {cuts} Schnitte übernehmen
            </DropdownItem>
          </>
        ) : null}
      </DropdownSubContent>
    </DropdownSub>
  );
}

// ---------------------------------------------------------------------------
// Clip on a track above the main track
// ---------------------------------------------------------------------------

const CORNER_LABELS: Record<CornerShape, string> = {
  square: "Eckig",
  rounded: "Abgerundet",
  circle: "Kreis",
};

function OverlayMenu({ id }: { id: string }) {
  const store = useEditorApi();
  const video = useVideoApi()!;
  const env = useEnv();
  const format = useEditor((s) => s.doc.format);
  const clip = useEditor((s) => s.doc.timeline?.overlays?.find((item) => item.id === id));
  const inside = usePlayheadIn(clip?.start ?? 0, clip ? overlayClipEnd(clip) : 0);
  if (!clip) return null;
  const asset = env.assets.get(clip.assetId);
  const source = { width: asset?.width ?? 1920, height: asset?.height ?? 1080 };
  const isVideo = clip.kind === "video";
  const hasAudio = isVideo && asset?.media?.hasAudio !== false;
  const thumbnail = asset?.thumbnailUrl ?? (clip.kind === "image" ? (asset?.url ?? null) : null);
  const corners = overlayCornerShape(clip);
  const faded = clip.fadeIn > 0 || clip.fadeOut > 0;
  const update = (recipe: (target: OverlayClip) => void, retime = false) =>
    updateOverlayClip(store, id, recipe, { retime });
  return (
    <>
      <DropdownLabel>
        {isVideo ? "Video" : "Bild"} auf Spur {clip.track + 1} ·{" "}
        {formatTimecode(overlayClipEnd(clip) - clip.start)}
      </DropdownLabel>
      <DropdownSub>
        <DropdownSubTrigger>
          <PictureInPicture2 /> Position
        </DropdownSubTrigger>
        <DropdownSubContent className="w-72">
          <div className="grid grid-cols-3 gap-0.5">
            {overlayLayoutsForFrame(format).map((layout) => (
              <DropdownItem
                key={layout}
                onSelect={() => applyOverlayLayout(store, id, layout, source)}
              >
                <span className="flex w-full flex-col items-center gap-1">
                  <OverlayLayoutPicture
                    layout={layout}
                    frame={format}
                    source={overlayBoxShape(clip, source)}
                    height={34}
                  />
                  <span className="text-center text-[10px] leading-tight text-zinc-600">
                    {OVERLAY_LAYOUT_LABELS[layout]}
                  </span>
                </span>
              </DropdownItem>
            ))}
          </div>
          {clip.device ? null : (
            <>
              <DropdownSeparator />
              <DropdownLabel>Ecken</DropdownLabel>
              {(["square", "rounded", "circle"] as const).map((shape) => (
                <DropdownCheckItem
                  key={shape}
                  checked={corners === shape}
                  onSelect={() => setOverlayCorners(store, id, shape)}
                  {...(shape === "circle"
                    ? { title: "Quadratischer Ausschnitt mit ganz runden Ecken (Facecam)" }
                    : {})}
                >
                  {CORNER_LABELS[shape]}
                </DropdownCheckItem>
              ))}
            </>
          )}
        </DropdownSubContent>
      </DropdownSub>
      <DropdownCheckItem
        checked={Boolean(clip.device)}
        onSelect={() =>
          setOverlayDevice(store, id, clip.device ? null : defaultClipDevice(asset), asset)
        }
        title="Bildschirmaufnahmen in einem Handy, Laptop oder Browserfenster (Einstellungen rechts)"
      >
        Im Gerät zeigen
      </DropdownCheckItem>
      {clip.device ? <DeviceViewSubmenu clip={clip} asset={asset} /> : null}
      <DropdownItem onSelect={() => moveSelectedToMain(store, video, clip.start)}>
        <Film /> In die Hauptspur verschieben
      </DropdownItem>
      <DropdownSeparator />
      <LookSubmenu clip={clip} track="overlay" thumbnail={thumbnail} />
      {isVideo ? (
        <SpeedSubmenu
          speeds={OVERLAY_SPEEDS}
          value={clip.speed}
          onPick={(speed) => update((target) => (target.speed = speed), true)}
        />
      ) : null}
      {hasAudio ? (
        <DropdownItem onSelect={() => update((target) => (target.muted = !target.muted))}>
          {clip.muted ? <Volume2 /> : <VolumeX />} {clip.muted ? "Ton an" : "Stumm schalten"}
        </DropdownItem>
      ) : null}
      <DropdownCheckItem
        checked={faded}
        onSelect={() =>
          update((target) => {
            target.fadeIn = faded ? 0 : 300;
            target.fadeOut = faded ? 0 : 300;
          })
        }
      >
        Sanft ein- und ausblenden
      </DropdownCheckItem>
      <DropdownSeparator />
      <SplitItem inside={inside} />
      <DropdownItem onSelect={() => duplicateSelectedClip(store, video)}>
        <CopyPlus /> Duplizieren <Shortcut>⌘D</Shortcut>
      </DropdownItem>
      <CutoutItem asset={asset} target={{ kind: "overlay", id }} />
      <PersonItems clip={clip} clipRef={{ track: "overlay", id }} asset={asset} />
      {clip.cutout ? null : <BehindPersonItem ids={[id]} />}
      <DropdownSeparator />
      <DeleteClipItem />
    </>
  );
}

/** The ready-made 3D views of a clip's device. */
function DeviceViewSubmenu({ clip, asset }: { clip: OverlayClip; asset: AssetInfo | undefined }) {
  const store = useEditorApi();
  const device = clip.device;
  if (!device) return null;
  const current = matchingPerspective(device.device, device.tilt);
  return (
    <DropdownSub>
      <DropdownSubTrigger>
        <Smartphone /> Geräteansicht
        <span className="truncate text-xs text-zinc-400">{deviceModel(device.device).name}</span>
      </DropdownSubTrigger>
      <DropdownSubContent className="w-48">
        {devicePerspectives(device.device).map((entry) => (
          <DropdownCheckItem
            key={entry.id}
            checked={current === entry.id}
            onSelect={() =>
              setOverlayDevice(store, clip.id, { ...device, tilt: entry.tilt }, asset)
            }
          >
            {entry.label}
          </DropdownCheckItem>
        ))}
      </DropdownSubContent>
    </DropdownSub>
  );
}

// ---------------------------------------------------------------------------
// Music
// ---------------------------------------------------------------------------

function AudioMenu({ id }: { id: string }) {
  const store = useEditorApi();
  const duration = useEditor((s) => videoDuration(s.doc));
  const clip = useEditor((s) => s.doc.timeline?.audio.find((item) => item.id === id));
  const inside = usePlayheadIn(clip?.start ?? 0, clip ? audioClipEnd(clip) : 0);
  if (!clip) return null;
  const update = (recipe: (target: AudioClip) => void) => updateAudioClip(store, id, recipe);
  const faded = clip.fadeIn > 0 || clip.fadeOut > 0;
  const pastEnd = audioClipEnd(clip) > duration + 1 && clip.start + MIN_CLIP_MS < duration;
  return (
    <>
      <DropdownLabel>Musik · {formatTimecode(clip.sourceOut - clip.sourceIn)}</DropdownLabel>
      <DropdownSub>
        <DropdownSubTrigger>
          <Volume2 /> Lautstärke
          <span className="text-xs text-zinc-400">{Math.round(clip.volume * 100)} %</span>
        </DropdownSubTrigger>
        <DropdownSubContent>
          {AUDIO_VOLUMES.map((volume) => (
            <DropdownCheckItem
              key={volume}
              checked={Math.abs(clip.volume - volume) < 0.01}
              onSelect={() => update((target) => (target.volume = volume))}
            >
              {Math.round(volume * 100)} %
            </DropdownCheckItem>
          ))}
          <DropdownSeparator />
          <DropdownCheckItem
            checked={clip.volume === 0}
            onSelect={() => update((target) => (target.volume = target.volume === 0 ? 0.5 : 0))}
          >
            Stumm
          </DropdownCheckItem>
        </DropdownSubContent>
      </DropdownSub>
      <DropdownCheckItem
        checked={clip.ducking}
        onSelect={() => update((target) => (target.ducking = !target.ducking))}
        title="Senkt die Musik automatisch unter die Stimme (nach der Spracherkennung)"
      >
        Leiser, wenn gesprochen wird
      </DropdownCheckItem>
      <DropdownCheckItem
        checked={faded}
        onSelect={() =>
          update((target) => {
            target.fadeIn = faded ? 0 : 1000;
            target.fadeOut = faded ? 0 : 2000;
          })
        }
      >
        Sanft ein- und ausblenden
      </DropdownCheckItem>
      {pastEnd ? (
        <DropdownItem
          onSelect={() =>
            update((target) => {
              target.sourceOut = target.sourceIn + Math.round(duration - target.start);
            })
          }
        >
          <ArrowRightToLine /> Am Videoende enden
        </DropdownItem>
      ) : null}
      <DropdownSeparator />
      <SplitItem inside={inside} />
      <DeleteClipItem />
    </>
  );
}

// ---------------------------------------------------------------------------
// Elements (texts, shapes, images, groups on the element lanes)
// ---------------------------------------------------------------------------

/** Moves the (unlocked) selected elements in time so the earliest starts at `t`. */
function moveElementsTo(store: EditorStore, ids: string[], t: number) {
  const { doc } = store.getState();
  const duration = videoDuration(doc);
  const elements = ids.flatMap((id) => (doc.elements[id] ? [doc.elements[id]!] : []));
  const timed = elements.filter((element) => element.time);
  const earliest = Math.min(...timed.map((element) => element.time!.start));
  store.getState().update((draft) => {
    for (const element of elements) {
      if (element.time) {
        const start = Math.max(0, element.time.start + t - earliest);
        const length = element.time.end - element.time.start;
        setElementTime(draft, element.id, { start, end: start + length }, doc, "move");
      } else {
        // Shown the whole time until now: from here to the end.
        const end = Math.max(t + MIN_CLIP_MS, duration);
        setElementTime(draft, element.id, { start: t, end }, doc, "resize");
      }
    }
  });
}

function extendElementsToEnd(store: EditorStore, ids: string[]) {
  const { doc } = store.getState();
  const duration = videoDuration(doc);
  store.getState().update((draft) => {
    for (const id of ids) {
      const time = doc.elements[id]?.time;
      if (!time || time.start + MIN_CLIP_MS > duration) continue;
      setElementTime(draft, id, { start: time.start, end: duration }, doc, "resize");
    }
  });
}

function ElementMenu({ id }: { id: string }) {
  const store = useEditorApi();
  const video = useVideoApi()!;
  const env = useEnv();
  const queryClient = useQueryClient();
  const doc = useEditor((s) => s.doc);
  const selection = useEditor((s) => s.selection);
  const ids = selection.includes(id) ? selection : [id];
  const elements = ids.flatMap((entry) => (doc.elements[entry] ? [doc.elements[entry]!] : []));
  if (elements.length === 0) return null;
  const movable = elements.filter((element) => !element.locked);
  const movableIds = movable.map((element) => element.id);
  const only: SceneElement | undefined = elements.length === 1 ? elements[0] : undefined;
  const duration = videoDuration(doc);
  const image = only?.type === "image" && !only.locked ? only : null;
  const imageAsset = image ? env.assets.get(image.assetId) : undefined;
  const canCutOut =
    image &&
    imageAsset?.kind === "image" &&
    !imageAsset.cutout &&
    imageAsset.status !== "processing";
  const hasGroup = elements.some((element) => element.type === "group");
  const locked = elements.every((element) => element.locked);
  const hidden = elements.every((element) => element.hidden);
  const timed = movable.some((element) => element.time);
  const shortOfEnd = movable.some(
    (element) => element.time && element.time.end < duration - 1 && element.time.start < duration,
  );
  return (
    <>
      <DropdownLabel>
        {elements.length === 1 ? "Element" : `${elements.length} Elemente`}
        {only?.time
          ? ` · ${formatTimecode(only.time.start)}–${formatTimecode(only.time.end)}`
          : only
            ? " · ganze Länge"
            : ""}
      </DropdownLabel>
      {movable.length > 0 ? (
        <>
          <DropdownItem onSelect={() => moveElementsTo(store, movableIds, video.getState().time)}>
            <Crosshair /> An den Abspielkopf verschieben
          </DropdownItem>
          {shortOfEnd ? (
            <DropdownItem onSelect={() => extendElementsToEnd(store, movableIds)}>
              <ArrowRightToLine /> Bis zum Videoende verlängern
            </DropdownItem>
          ) : null}
          {timed ? (
            <DropdownItem
              onSelect={() =>
                store.getState().update((draft) => {
                  for (const entry of movableIds) setElementTime(draft, entry, undefined);
                })
              }
            >
              <Maximize2 /> Über die ganze Länge zeigen
            </DropdownItem>
          ) : null}
          <DropdownSeparator />
        </>
      ) : null}
      {canGroup(doc, ids) ? (
        <DropdownItem onSelect={() => groupSelection(store)}>
          <Group /> Gruppieren <Shortcut>⌘G</Shortcut>
        </DropdownItem>
      ) : null}
      {hasGroup ? (
        <DropdownItem onSelect={() => ungroupSelection(store)}>
          <Ungroup /> Gruppierung aufheben <Shortcut>⇧⌘G</Shortcut>
        </DropdownItem>
      ) : null}
      <DropdownItem onSelect={() => requestSaveBlock(ids)}>
        <BookmarkPlus /> Als Baustein speichern …
      </DropdownItem>
      <BehindPersonItem ids={ids} />
      {canCutOut ? (
        <DropdownItem
          onSelect={() =>
            void startCutout({
              orgId: env.orgId,
              store,
              queryClient,
              target: { kind: "element", id: image.id },
              source: imageAsset,
            })
          }
        >
          <Wand2 /> Hintergrund entfernen
        </DropdownItem>
      ) : null}
      <DropdownSeparator />
      <DropdownItem onSelect={() => duplicateElements(store)}>
        <CopyPlus /> Duplizieren <Shortcut>⌘D</Shortcut>
      </DropdownItem>
      <DropdownItem
        onSelect={() => {
          const text = copySelection(store);
          if (text) void navigator.clipboard?.writeText(text).catch(() => undefined);
        }}
      >
        <Copy /> Kopieren <Shortcut>⌘C</Shortcut>
      </DropdownItem>
      <DropdownItem onSelect={() => pasteData(store)}>
        <ClipboardPaste /> Einfügen <Shortcut>⌘V</Shortcut>
      </DropdownItem>
      <DropdownSeparator />
      <DropdownItem onSelect={() => toggleHidden(store, ids)}>
        {hidden ? <Eye /> : <EyeOff />} {hidden ? "Einblenden" : "Ausblenden"}
      </DropdownItem>
      <DropdownItem onSelect={() => toggleLock(store)}>
        {locked ? <LockOpen /> : <Lock />} {locked ? "Entsperren" : "Sperren"}
        <Shortcut>⌘L</Shortcut>
      </DropdownItem>
      <DropdownItem danger onSelect={() => deleteSelection(store)}>
        <Trash2 /> Löschen <Shortcut>⌫</Shortcut>
      </DropdownItem>
    </>
  );
}

// ---------------------------------------------------------------------------
// Caption track and empty space
// ---------------------------------------------------------------------------

function CaptionsMenu() {
  const store = useEditorApi();
  const video = useVideoApi()!;
  const { captionsElement, transcripts, words } = useCaptions();
  const available = useCaptionsAvailable();
  if (!captionsElement) {
    return available ? (
      <CaptionItems />
    ) : (
      <DropdownItem disabled>
        <Type /> Untertitel entstehen aus dem Ton der Videos
      </DropdownItem>
    );
  }
  return (
    <>
      <CaptionItems />
      <DropdownItem onSelect={() => openCaptions(store, video, { tab: "style" })}>
        <Sparkles /> Stil ändern
      </DropdownItem>
      {words.length > 0 ? (
        <DropdownItem
          onSelect={() => {
            const removed = removeSilences(store, transcripts, SILENCE_MS);
            if (removed > 0) {
              video.getState().seek(0);
              toast.success(
                `${(removed / 1000).toLocaleString("de-DE", { maximumFractionDigits: 1 })} s Stille entfernt`,
              );
            } else toast.info("Keine so langen Pausen gefunden");
          }}
        >
          <AudioLines /> Pausen über {(SILENCE_MS / 1000).toLocaleString("de-DE")} s entfernen
        </DropdownItem>
      ) : null}
      <DropdownSeparator />
      <DropdownItem
        danger
        onSelect={() => {
          video.getState().selectClip(null);
          store.getState().select([captionsElement.id]);
          deleteSelection(store);
        }}
      >
        <Trash2 /> Untertitel entfernen
      </DropdownItem>
    </>
  );
}

function EmptyMenu({ t }: { t: number }) {
  const store = useEditorApi();
  const video = useVideoApi()!;
  const captions = useCaptionsAvailable();
  return (
    <>
      <DropdownItem onSelect={() => video.getState().seek(t)}>
        <Crosshair /> Abspielkopf hierher
        <Shortcut>{formatTimecode(t)}</Shortcut>
      </DropdownItem>
      <DropdownItem onSelect={() => pasteData(store)}>
        <ClipboardPaste /> Einfügen <Shortcut>⌘V</Shortcut>
      </DropdownItem>
      {captions ? (
        <>
          <DropdownSeparator />
          <CaptionItems />
        </>
      ) : null}
      <DropdownSeparator />
      <DropdownItem onSelect={() => openLayouts()}>
        <SlidersHorizontal /> Bildaufteilung wählen …
      </DropdownItem>
    </>
  );
}
